// ================================================================
// 🗂️ FILTER DRAWER - Drawer de Filtros Avançados
// ================================================================

import React, { useState, useEffect } from 'react';
import { Offcanvas, Form, Button, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';

/**
 * 🗂️ FilterDrawer - Drawer com filtros avançados
 * 
 * @param {Object} props
 * @param {boolean} props.show - Mostrar/ocultar drawer
 * @param {Function} props.onHide - Callback ao fechar
 * @param {Function} props.onApply - Callback ao aplicar filtros
 * @param {Object} props.currentFilters - Filtros atuais
 */
const FilterDrawer = ({ show, onHide, onApply, currentFilters = {} }) => {
  const [filters, setFilters] = useState({
    product_id: '',
    payment_method: '',
    sale_status: '',
    region: '',
    customDateRange: {
      start: '',
      end: ''
    }
  });

  // Sincronizar com filtros atuais quando abrir
  useEffect(() => {
    if (show && currentFilters) {
      setFilters({
        product_id: currentFilters.product_id || '',
        payment_method: currentFilters.payment_method || '',
        sale_status: currentFilters.sale_status || '',
        region: currentFilters.region || '',
        customDateRange: currentFilters.customDateRange || { start: '', end: '' }
      });
    }
  }, [show, currentFilters]);

  /**
   * Atualiza um filtro específico
   */
  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  /**
   * Atualiza intervalo de data personalizado
   */
  const updateDateRange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      customDateRange: {
        ...prev.customDateRange,
        [field]: value
      }
    }));
  };

  /**
   * Limpa todos os filtros
   */
  const clearFilters = () => {
    setFilters({
      product_id: '',
      payment_method: '',
      sale_status: '',
      region: '',
      customDateRange: { start: '', end: '' }
    });
  };

  /**
   * Aplica filtros
   */
  const handleApply = () => {
    onApply && onApply(filters);
  };

  return (
    <Offcanvas 
      show={show} 
      onHide={onHide} 
      placement="end"
      style={{ width: '450px' }}
    >
      <Offcanvas.Header 
        closeButton
        style={{
          borderBottom: '2px solid #e9ecef',
          padding: '24px'
        }}
      >
        <Offcanvas.Title style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#212529',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <Icon icon="mdi:filter" style={{ fontSize: '24px', color: '#007bff' }} />
          Filtros Avançados
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ padding: '24px' }}>
        <Form>
          {/* Produto Específico */}
          <Form.Group className="mb-4">
            <Form.Label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon icon="mdi:package-variant" />
              Produto Específico
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="ID ou nome do produto"
              value={filters.product_id}
              onChange={(e) => updateFilter('product_id', e.target.value)}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px'
              }}
            />
          </Form.Group>

          {/* Método de Pagamento */}
          <Form.Group className="mb-4">
            <Form.Label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon icon="mdi:credit-card" />
              Método de Pagamento
            </Form.Label>
            <Form.Select
              value={filters.payment_method}
              onChange={(e) => updateFilter('payment_method', e.target.value)}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px'
              }}
            >
              <option value="">Todos</option>
              <option value="credit_card">Cartão de Crédito</option>
              <option value="pix">PIX</option>
              <option value="boleto">Boleto</option>
            </Form.Select>
          </Form.Group>

          {/* Status da Venda */}
          <Form.Group className="mb-4">
            <Form.Label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon icon="mdi:check-circle" />
              Status da Venda
            </Form.Label>
            <Form.Select
              value={filters.sale_status}
              onChange={(e) => updateFilter('sale_status', e.target.value)}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px'
              }}
            >
              <option value="">Todos</option>
              <option value="paid">Pago</option>
              <option value="pending">Pendente</option>
              <option value="cancelled">Cancelado</option>
              <option value="refunded">Reembolsado</option>
            </Form.Select>
          </Form.Group>

          {/* Região Geográfica */}
          <Form.Group className="mb-4">
            <Form.Label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon icon="mdi:map-marker" />
              Região Geográfica
            </Form.Label>
            <Form.Select
              value={filters.region}
              onChange={(e) => updateFilter('region', e.target.value)}
              style={{
                borderRadius: '8px',
                border: '2px solid #e9ecef',
                padding: '10px 12px'
              }}
            >
              <option value="">Todas</option>
              <option value="SP">São Paulo</option>
              <option value="RJ">Rio de Janeiro</option>
              <option value="MG">Minas Gerais</option>
              <option value="RS">Rio Grande do Sul</option>
              <option value="PR">Paraná</option>
              <option value="SC">Santa Catarina</option>
              <option value="BA">Bahia</option>
              <option value="PE">Pernambuco</option>
              <option value="CE">Ceará</option>
              <option value="other">Outros</option>
            </Form.Select>
          </Form.Group>

          {/* Intervalo de Tempo Personalizado */}
          <Form.Group className="mb-4">
            <Form.Label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#495057',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Icon icon="mdi:calendar-range" />
              Intervalo de Tempo Personalizado
            </Form.Label>
            <Row className="g-2">
              <Col xs={6}>
                <Form.Control
                  type="date"
                  value={filters.customDateRange.start}
                  onChange={(e) => updateDateRange('start', e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e9ecef',
                    padding: '10px 12px'
                  }}
                />
              </Col>
              <Col xs={6}>
                <Form.Control
                  type="date"
                  value={filters.customDateRange.end}
                  onChange={(e) => updateDateRange('end', e.target.value)}
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e9ecef',
                    padding: '10px 12px'
                  }}
                />
              </Col>
            </Row>
          </Form.Group>

          {/* Botões de Ação */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '32px'
          }}>
            <Button
              variant="outline-secondary"
              onClick={clearFilters}
              style={{
                flex: 1,
                borderRadius: '8px',
                padding: '12px',
                fontWeight: '600'
              }}
            >
              <Icon icon="mdi:close" style={{ marginRight: '8px' }} />
              Limpar
            </Button>
            <Button
              variant="primary"
              onClick={handleApply}
              style={{
                flex: 1,
                borderRadius: '8px',
                padding: '12px',
                fontWeight: '600'
              }}
            >
              <Icon icon="mdi:check" style={{ marginRight: '8px' }} />
              Aplicar
            </Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default FilterDrawer;

